'use client';

import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import { useContext } from 'react';
import UserVehicleContext, {
  useVehiclePaymentTerms
} from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import {
  VehicleDetailsFuelType,
  VehicleDetailsStatus
} from '~/contracts/dealership/ShiftDigital';
import useTrackAdobePageLoadEvent from '~/hooks/dealership/useTrackAdobePageLoadEvent';
import useTrackShiftDigitalPageViewEvent from '~/hooks/dealership/useTrackShiftDigitalPageViewEvent';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';

function getFuelType(fuelDescription: string): VehicleDetailsFuelType {
  switch (fuelDescription) {
    case 'Diesel':
      return 'Diesel';
    case 'Electric':
      return 'Electric';
    case 'Hybrid':
      return 'Hybrid';
    default:
      return 'Gasoline';
  }
}

function getVehicleStatus(offering: VehicleOffering): VehicleDetailsStatus {
  return offering === VehicleOffering.New ? 'New' : 'Used';
}

export default function DealershipPageEventWithVehicle() {
  const { vehicleAvailability, vehiclePricingInfo } =
    useContext(UserVehicleContext);
  const {
    drivetrainDescription,
    drivetrainDescriptionOEM = drivetrainDescription || '',
    engineDescription,
    engineDescriptionOEM = engineDescription || '',
    exteriorColor = '',
    fuelDescription = '',
    interiorColor = '',
    make = '',
    model = '',
    msrp = 0,
    offering = VehicleOffering.New,
    transmission,
    transmissionOEM = transmission || '',
    trim = '',
    vin = '',
    year = 0
  } = useGetVehicleDetails();
  const isNotAvailableForImmediatePurchase =
    useIsNotAvailableForImmediatePurchase();
  const { incentivizedPrice = 0 } = useVehiclePaymentTerms();

  const deliveryStatus = isNotAvailableForImmediatePurchase
    ? 'out_of_stock'
    : 'in_stock';
  const stockStatus = isNotAvailableForImmediatePurchase
    ? 'InProduction'
    : 'OnTheLot';

  useTrackAdobePageLoadEvent(vehicleAvailability.isLoaded, {
    vehicle: {
      condition: offering,
      deliveryStatus,
      make,
      model,
      trim,
      vin,
      year
    }
  });

  useTrackShiftDigitalPageViewEvent(
    vehicleAvailability.isLoaded && vehiclePricingInfo.isLoaded,
    {
      displayedPrice: incentivizedPrice,
      driveTrain: drivetrainDescriptionOEM,
      engine: engineDescriptionOEM,
      exteriorColor,
      fuelType: getFuelType(fuelDescription),
      interiorColor,
      make,
      model,
      msrp,
      status: getVehicleStatus(offering),
      stockStatus,
      transmission: transmissionOEM,
      trim,
      vin,
      year
    }
  );

  return null;
}
